import { useEffect, useRef, useState } from "react";
import { Mic, Square, Camera, Image as ImageIcon, FileText, Upload, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, isPaywallError } from "@/lib/api";
import { VaultToggle } from "@/components/VaultSave";
import { vaultTitle } from "@/lib/vault";

const FREE_HINT = "No plano Free: textos, fotos e áudios. Protegido por PIN.";
const vaultHint = (v) => (v.isPremium ? undefined : FREE_HINT);

export function TextCapture({ onAnalyze, initial = "", vault }) {
  const [text, setText] = useState(initial);
  const go = () => (vault?.toVault ? vault.onSaveVault([{ title: vaultTitle(text), content: text }]) : onAnalyze({ text }));
  return (
    <div className="ga-fade-up px-5">
      <label htmlFor="memory-text" className="text-lg font-semibold">O que você quer guardar?</label>
      <textarea id="memory-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} autoFocus placeholder="Ex: Guardei minha chave reserva na caixa azul do armário." className="ga-input mt-3 resize-none text-base" data-testid="new-memory-text-input" />
      <p className="ga-muted mt-2 text-xs">Dica: fale como se estivesse contando para alguém. A gente organiza.</p>
      {vault && <VaultToggle on={vault.toVault} onChange={vault.setToVault} hint={vaultHint(vault)} />}
      <button onClick={go} disabled={text.trim().length < 3 || vault?.saving} className="ga-btn-primary mt-6 w-full text-base" data-testid="new-memory-analyze-button">{vault?.toVault ? (vault.saving ? "Guardando..." : "Salvar na Pasta Segura") : "Guardar"}</button>
    </div>
  );
}

export function VoiceCapture({ onAnalyze, onPaywall, vault }) {
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [text, setText] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);

  useEffect(() => () => { clearInterval(timer.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mime });
        setAudioBlob(blob);
        setState("transcribing");
        const fd = new FormData();
        fd.append("file", blob, mime.includes("mp4") ? "audio.mp4" : "audio.webm");
        try {
          const { data } = await api.post("/ai/transcribe", fd);
          setText(data.text);
          setState("review");
        } catch (e) {
          if (isPaywallError(e)) onPaywall(errorMessage(e));
          else toast.error(errorMessage(e));
          setState("idle");
        }
      };
      rec.start();
      recRef.current = rec;
      setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setState("recording");
    } catch {
      toast.error("Não consegui acessar o microfone. Verifique as permissões do navegador.");
    }
  };
  const stop = () => { clearInterval(timer.current); recRef.current?.stop(); };
  const submitVoice = async () => {
    if (vault?.toVault) {
      const name = audioBlob?.type.includes("mp4") ? "gravacao.m4a" : "gravacao.webm";
      const items = [{ title: vaultTitle(text, "Áudio"), content: text }];
      if (audioBlob) items.push({ title: vaultTitle(text, "Áudio"), file: new File([audioBlob], name, { type: audioBlob.type }) });
      return vault.onSaveVault(items);
    }
    setSaving(true);
    let file_ids = [];
    if (audioBlob) {
      try {
        const fd = new FormData();
        fd.append("file", audioBlob, audioBlob.type.includes("mp4") ? "gravacao.m4a" : "gravacao.webm");
        fd.append("kind", "audio");
        const { data } = await api.post("/files/upload", fd);
        file_ids = [data.id];
      } catch (e) {
        toast.error(errorMessage(e, "Não consegui guardar o áudio, mas a transcrição será salva."));
      }
    }
    setSaving(false);
    onAnalyze({ text, source_type: "voice", file_ids });
  };

  if (state === "review") {
    return (
      <div className="ga-fade-up px-5">
        <h2 className="text-lg font-semibold">Foi isso que entendi:</h2>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className="ga-input mt-3 resize-none" data-testid="voice-transcript-input" />
        <p className="ga-muted mt-2 text-xs">Você pode corrigir o texto antes de continuar.</p>
        {vault && <VaultToggle on={vault.toVault} onChange={vault.setToVault} hint={vaultHint(vault)} />}
        <div className="mt-6 flex gap-3">
          <button onClick={() => setState("idle")} className="ga-btn-ghost flex-1" data-testid="voice-retry-button">Gravar de novo</button>
          <button onClick={submitVoice} disabled={saving || vault?.saving} className="ga-btn-primary flex-[1.4]" data-testid="voice-analyze-button">{saving ? "Enviando áudio..." : vault?.toVault ? (vault.saving ? "Guardando..." : "Salvar na Pasta Segura") : "Guardar"}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ga-fade-up flex flex-col items-center px-5 pt-8 text-center">
      <h2 className="text-lg font-semibold">{state === "recording" ? "Estou ouvindo..." : state === "transcribing" ? "Entendendo o que você disse..." : "Toque e fale"}</h2>
      <p className="ga-muted mt-1 text-sm">Ex: “Guarda aí que troquei o filtro da geladeira hoje.”</p>
      <div className="relative mt-12 flex items-center justify-center">
        {state === "recording" && <span className="ga-pulse-ring absolute h-32 w-32 rounded-full" style={{ background: "#4E78FF" }} />}
        <button
          onClick={state === "recording" ? stop : start}
          disabled={state === "transcribing"}
          className="relative flex h-32 w-32 items-center justify-center rounded-full text-white transition-transform active:scale-95 disabled:opacity-60"
          style={{ background: state === "recording" ? "#E5484D" : "#4E78FF", boxShadow: "0 16px 32px -12px rgba(78,120,255,.8)" }}
          aria-label={state === "recording" ? "Parar gravação" : "Começar gravação"}
          data-testid="voice-record-button"
        >
          {state === "recording" ? <Square size={40} fill="white" /> : <Mic size={44} />}
        </button>
      </div>
      <p className="mt-6 text-2xl font-semibold tabular-nums" data-testid="voice-timer">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</p>
      <p className="ga-muted mt-2 text-xs">{state === "recording" ? "Toque para parar" : "Grave até 1 minuto"}</p>
    </div>
  );
}

export function CameraChoice({ onPick, isPremium }) {
  return (
    <div className="ga-fade-up px-5">
      <h2 className="text-lg font-semibold">O que você quer registrar?</h2>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <button onClick={() => onPick("foto")} className="flex h-36 flex-col items-center justify-center gap-2 rounded-3xl text-white" style={{ background: "#13C9A3" }} data-testid="camera-choice-photo"><Camera size={30} /> <span className="font-semibold">Foto</span></button>
        <button onClick={() => onPick("video")} className="ga-surface flex h-36 flex-col items-center justify-center gap-2 rounded-3xl" data-testid="camera-choice-video"><Video size={30} /> <span className="font-semibold">Vídeo</span></button>
      </div>
      <p className="ga-muted mt-3 text-xs">Fotos até 20 MB. Vídeos até 200 MB{isPremium ? "." : ", disponíveis no plano Premium."}</p>
    </div>
  );
}

export function PhotoCapture({ onAnalyze, onPaywall, vault, media = "photo" }) {
  const isVideo = media === "video";
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const go = async () => {
    if (vault?.toVault) return vault.onSaveVault([{ title: vaultTitle(desc, file.name), file }]);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", media);
      const { data } = await api.post("/files/upload", fd);
      await onAnalyze({ text: desc, file_ids: [data.id], source_type: media });
    } catch (e) {
      if (isPaywallError(e) && onPaywall) onPaywall(errorMessage(e));
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ga-fade-up px-5">
      {!preview ? (
        <>
          <h2 className="text-lg font-semibold">{isVideo ? "Grave ou escolha um vídeo" : "Fotografe ou escolha uma imagem"}</h2>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <label className="flex h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-3xl text-white" style={{ background: "#13C9A3" }} data-testid="photo-camera-label">
              {isVideo ? <Video size={30} /> : <Camera size={30} />} <span className="font-semibold">{isVideo ? "Gravar" : "Câmera"}</span>
              <input type="file" accept={isVideo ? "video/*" : "image/*"} capture="environment" onChange={pick} className="hidden" data-testid="photo-camera-input" />
            </label>
            <label className="ga-surface flex h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-3xl" data-testid="photo-gallery-label">
              <ImageIcon size={30} /> <span className="font-semibold">Galeria</span>
              <input type="file" accept={isVideo ? "video/*" : "image/*"} onChange={pick} className="hidden" data-testid="photo-gallery-input" />
            </label>
          </div>
          {isVideo && <p className="ga-muted mt-3 text-xs">Vídeos até 200 MB. Disponível no plano Premium.</p>}
        </>
      ) : (
        <>
          {isVideo ? <video src={preview} controls playsInline className="max-h-72 w-full rounded-3xl bg-black object-contain" data-testid="video-preview" /> : <img src={preview} alt="Prévia da foto" className="max-h-72 w-full rounded-3xl object-cover" data-testid="photo-preview" />}
          <button onClick={() => { setPreview(null); setFile(null); }} className="ga-muted mt-2 text-xs" data-testid="photo-change-button">{isVideo ? "Trocar vídeo" : "Trocar imagem"}</button>
          <label htmlFor="photo-desc" className="mt-4 block text-sm font-semibold">O que é isso? (opcional)</label>
          <textarea id="photo-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Ex: É aqui que guardei os documentos do apartamento." className="ga-input mt-2 resize-none" data-testid="photo-description-input" />
          {vault && <VaultToggle on={vault.toVault} onChange={vault.setToVault} hint={isVideo && !vault.isPremium ? "Vídeos na Pasta Segura estão disponíveis no plano Premium." : vaultHint(vault)} />}
          <button onClick={go} disabled={busy || vault?.saving} className="ga-btn-primary mt-5 w-full text-base" data-testid="photo-analyze-button">{busy ? "Enviando..." : vault?.toVault ? (vault.saving ? "Guardando..." : "Salvar na Pasta Segura") : "Guardar"}</button>
        </>
      )}
    </div>
  );
}

export function DocumentCapture({ onResult, onPaywall, isPremium, vault }) {
  const [file, setFile] = useState(null);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (vault?.toVault) return vault.onSaveVault([{ title: vaultTitle(desc, file.name), file }]);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", desc);
      const { data } = await api.post("/ai/document", fd);
      onResult(data);
    } catch (e) {
      if (isPaywallError(e)) onPaywall(errorMessage(e));
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ga-fade-up px-5">
      <h2 className="text-lg font-semibold">Envie um documento</h2>
      <p className="ga-muted mt-1 text-sm">Imagem, PDF, Word, PowerPoint, planilha ou vídeo. Em PDFs e imagens, lemos e extraímos o que importa: produto, loja, datas, validade e valor.</p>
      <label className="ga-card mt-5 flex h-40 cursor-pointer flex-col items-center justify-center gap-2 border-dashed" style={{ borderWidth: 2 }} data-testid="document-pick-label">
        {file ? <FileText size={30} style={{ color: "#0A6373" }} /> : <Upload size={30} className="ga-muted" />}
        <span className="max-w-[80%] truncate text-sm font-medium">{file ? file.name : "Toque para escolher"}</span>
        <input type="file" accept="image/*,application/pdf,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.odt,.ods,.odp,video/*,.mp4,.mov" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="document-file-input" />
      </label>
      <p className="ga-muted mt-2 text-[11px]">Limites: documentos até 25 MB, vídeos até 200 MB.{!isPremium && " Documentos e vídeos estão disponíveis no plano Premium."}</p>
      <label htmlFor="doc-desc" className="mt-4 block text-sm font-semibold">Alguma observação? (opcional)</label>
      <input id="doc-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Ex: Garantia da geladeira nova" className="ga-input mt-2" data-testid="document-description-input" />
      <p className="ga-muted mt-3 text-xs">Não guardamos dados sensíveis como CPF ou números de cartão. Você revisa tudo antes de salvar.</p>
      {vault && <VaultToggle on={vault.toVault} onChange={vault.setToVault} hint={isPremium ? "Guardado sem leitura por IA. Protegido por PIN." : "No plano Free: só imagens na Pasta Segura. Documentos e vídeos no Premium."} />}
      <button onClick={go} disabled={!file || busy || vault?.saving} className="ga-btn-primary mt-5 w-full text-base" data-testid="document-analyze-button">{busy ? "Lendo o documento..." : vault?.toVault ? (vault.saving ? "Guardando..." : "Salvar na Pasta Segura") : "Ler documento"}</button>
    </div>
  );
}
